import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { pessoaService } from '../../services/pessoaService';
import cursoService from '../../services/cursoService';
import produtoService from '../../services/produtoService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../styles/LancamentoNotasIndividual.css';

// Componente de Busca/Select Customizado
const SearchableSelect = ({ 
  label, 
  placeholder, 
  value,
  options, 
  onSelect, 
  loading = false,
  disabled = false,
  required = false,
  noOptionsMessage = "Nenhuma opção encontrada",
  valueKey = 'id',
  labelKey = 'nome'
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  
  // Encontrar o item selecionado para exibir o nome
  const selectedItem = options.find(opt => {
    const optValue = opt[valueKey] || opt.idExtensao || opt.idPessoa || opt.idProduto;
    return String(optValue) === String(value);
  });
  
  // Atualizar searchValue quando houver seleção ou limpeza
  useEffect(() => {
    if (!showDropdown) {
      if (selectedItem) {
        setSearchValue(selectedItem[labelKey] || '');
      } else if (!value) {
        // Se value foi limpo, limpar o searchValue também
        setSearchValue('');
      }
    }
  }, [selectedItem, showDropdown, labelKey, value]);
  
  // Filtrar opções baseado na busca
  const filteredOptions = options.filter(opt => {
    const label = (opt[labelKey] || '').toLowerCase();
    return label.includes(searchValue.toLowerCase());
  });
  
  const handleKeyDown = (e) => {
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !showDropdown) {
      e.preventDefault();
      setSearchValue('');
      setShowDropdown(true);
      return;
    }
    if (!showDropdown || filteredOptions.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          const next = prev < filteredOptions.length - 1 ? prev + 1 : 0;
          scrollToIndex(next);
          return next;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          const next = prev > 0 ? prev - 1 : filteredOptions.length - 1;
          scrollToIndex(next);
          return next;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowDropdown(false);
        break;
    }
  };
  
  const handleSelect = (option) => {
    console.log('🔍 SearchableSelect - handleSelect:', { option, valueKey, labelKey });
    console.log('🔍 Campos disponíveis:', Object.keys(option));
    
    // Tenta extrair o valor usando diferentes estratégias
    let optValue = option[valueKey];
    if (!optValue) {
      // Fallback para campos comuns
      optValue = option.idExtensao || option.idPessoa || option.idProduto || option.id;
    }
    
    console.log('🔍 Valor extraído:', optValue);
    
    onSelect(optValue);
    setSearchValue(option[labelKey] || '');
    setShowDropdown(false);
    setHighlightedIndex(-1);
  };
  
  const scrollToIndex = (index) => {
    requestAnimationFrame(() => {
      if (!dropdownRef.current) return;
      const items = dropdownRef.current.children;
      if (items[index]) {
        items[index].scrollIntoView({ block: 'nearest' });
      }
    });
  };

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [showDropdown]);
  
  return (
    <div className="form-group">
      <label>
        {label} {required && <span className="required">*</span>}
      </label>
      <div className="searchable-select-container">
        <input
          type="text"
          value={showDropdown ? searchValue : (selectedItem?.[labelKey] || searchValue)}
          onChange={(e) => {
            setSearchValue(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => {
            setSearchValue('');
            setShowDropdown(true);
          }}
          onBlur={() => {
            setTimeout(() => setShowDropdown(false), 200);
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled || loading}
          className="form-control searchable-input"
          placeholder={loading ? 'Carregando...' : placeholder}
          required={required}
        />
        <span 
          className="dropdown-toggle-icon"
          onClick={() => !disabled && !loading && setShowDropdown(!showDropdown)}
        >
          {showDropdown ? '×' : '▼'}
        </span>
        
        {showDropdown && filteredOptions.length > 0 && (
          <div className="searchable-dropdown" ref={dropdownRef}>
            {filteredOptions.map((option, index) => {
              const optValue = option[valueKey] || option.idExtensao || option.idPessoa || option.idProduto;
              const optLabel = option[labelKey] || '';
              
              return (
                <div
                  key={optValue}
                  onClick={() => handleSelect(option)}
                  className={`dropdown-option ${index === highlightedIndex ? 'highlighted' : ''}`}
                >
                  {optLabel}
                </div>
              );
            })}
          </div>
        )}
        
        {showDropdown && filteredOptions.length === 0 && !loading && (
          <div className="searchable-dropdown">
            <div className="dropdown-option no-results">
              {noOptionsMessage}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function LancamentoNotasIndividual() {

  // Estados dos dropdowns
  const [extensoes, setExtensoes] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [disciplinas, setDisciplinas] = useState([]);

  // Estados do formulário
  const [formData, setFormData] = useState({
    extensao: '',
    aluno: '',
    curso: '',
    disciplina: '',
    dataRealizacao: new Date().toISOString().split('T')[0],
    cargaHoraria: '',
    media: ''
  });

  // Estados de controle
  const [loading, setLoading] = useState(false);
  const [loadingDropdown, setLoadingDropdown] = useState({});
  const [ultimosLancamentos, setUltimosLancamentos] = useState([]);

  // Refs para navegação por Enter
  const extensaoRef = useRef(null);
  const alunoRef = useRef(null);
  const cursoRef = useRef(null);
  const disciplinaRef = useRef(null);
  const dataRef = useRef(null);
  const cargaRef = useRef(null);
  const mediaRef = useRef(null);

  // Carregar extensões do usuário, cursos e disciplinas
  useEffect(() => {
    carregarExtensoes();
    carregarCursos();
    carregarDisciplinas();
  }, []);

  // Carregar alunos quando extensão mudar
  useEffect(() => {
    if (formData.extensao) {
      carregarAlunos(formData.extensao);
    } else {
      setAlunos([]);
    }
  }, [formData.extensao]);

  const carregarExtensoes = async () => {
    try {
      setLoadingDropdown(prev => ({ ...prev, extensao: true }));
      const response = await api.get('/usuarios/me/extensoes');
      const extensoesData = Array.isArray(response.data?.extensoes) 
        ? response.data.extensoes 
        : Array.isArray(response.data) 
        ? response.data 
        : [];
      setExtensoes(extensoesData);
      console.log('✅ Extensões carregadas:', extensoesData.length);
      if (extensoesData.length > 0) {
        console.log('📋 Primeira extensão:', extensoesData[0]);
        console.log('📋 Campos da extensão:', Object.keys(extensoesData[0]));
      }
    } catch (error) {
      console.error('Erro ao carregar extensões:', error);
      toast.error('Erro ao carregar extensões');
    } finally {
      setLoadingDropdown(prev => ({ ...prev, extensao: false }));
    }
  };

  const carregarAlunos = async (idExtensao) => {
    try {
      setLoadingDropdown(prev => ({ ...prev, aluno: true }));
      console.log('🔍 Carregando alunos da extensão:', idExtensao, typeof idExtensao);
      
      // Garantir que é número
      const extensaoId = parseInt(idExtensao);
      if (isNaN(extensaoId)) {
        console.error('❌ ID de extensão inválido:', idExtensao);
        toast.error('ID de extensão inválido');
        setAlunos([]);
        return;
      }
      
      console.log('🔢 ID convertido para número:', extensaoId);
      
      // Buscar apenas alunos (dtype='Aluno') da extensão selecionada
      const response = await pessoaService.getByExtensao(extensaoId, 'Aluno');
      console.log('📦 Resposta completa:', response);
      
      const alunosData = Array.isArray(response.data) 
        ? response.data 
        : Array.isArray(response) 
        ? response 
        : [];
      
      console.log('✅ Total de alunos encontrados:', alunosData.length);
      if (alunosData.length > 0) {
        console.log('👤 Primeiro aluno:', alunosData[0]);
      }
      
      setAlunos(alunosData);
    } catch (error) {
      console.error('❌ Erro ao carregar alunos:', error);
      toast.error('Erro ao carregar alunos');
      setAlunos([]);
    } finally {
      setLoadingDropdown(prev => ({ ...prev, aluno: false }));
    }
  };

  const carregarCursos = async () => {
    try {
      setLoadingDropdown(prev => ({ ...prev, curso: true }));
      const response = await cursoService.list({ limit: 1000 });
      const cursosData = Array.isArray(response.data) 
        ? response.data 
        : [];
      
      // Filtrar apenas cursos Básico, Médio, Avançado e Convalidação
      const cursosFiltrados = cursosData.filter(curso => {
        const nomeCurso = curso.nome?.toLowerCase() || '';
        return nomeCurso.includes('básico') || 
               nomeCurso.includes('basico') || 
               nomeCurso.includes('médio') || 
               nomeCurso.includes('medio') || 
               nomeCurso.includes('avançado') || 
               nomeCurso.includes('avancado') ||
               nomeCurso.includes('convalidação') ||
               nomeCurso.includes('convalidacao');
      });
      
      setCursos(cursosFiltrados);
      console.log('✅ Cursos carregados (Básico/Médio/Avançado/Convalidação):', cursosFiltrados.length);
      console.log('📋 Cursos:', cursosFiltrados.map(c => c.nome));
    } catch (error) {
      console.error('Erro ao carregar cursos:', error);
      toast.error('Erro ao carregar cursos');
      setCursos([]);
    } finally {
      setLoadingDropdown(prev => ({ ...prev, curso: false }));
    }
  };

  const carregarDisciplinas = async () => {
    try {
      setLoadingDropdown(prev => ({ ...prev, disciplina: true }));
      
      console.log('🔍 Carregando disciplinas (produtos tipo 1)...');
      
      // Buscar produtos do tipo 1 (livros)
      const response = await produtoService.list({ 
        tipoProduto: 1,
        limit: 1000
      });
      
      console.log('📦 Resposta completa da API:', response);
      console.log('📦 response.data:', response.data);
      
      // Tentar diferentes estruturas de resposta
      let disciplinasData = [];
      
      if (response.data?.produtos) {
        disciplinasData = response.data.produtos;
      } else if (response.data?.data?.produtos) {
        disciplinasData = response.data.data.produtos;
      } else if (Array.isArray(response.data?.data)) {
        disciplinasData = response.data.data;
      } else if (Array.isArray(response.data)) {
        disciplinasData = response.data;
      } else if (Array.isArray(response)) {
        disciplinasData = response;
      }
      
      console.log('📋 Disciplinas extraídas:', disciplinasData.length);
      
      if (disciplinasData.length > 0) {
        console.log('📚 Primeira disciplina:', disciplinasData[0]);
        console.log('📚 Campos disponíveis:', Object.keys(disciplinasData[0]));
      }
      
      setDisciplinas(disciplinasData);
      console.log('✅ Disciplinas (livros) carregadas:', disciplinasData.length);
    } catch (error) {
      console.error('❌ Erro ao carregar disciplinas:', error);
      console.error('❌ Erro completo:', error.response?.data || error.message);
      toast.error('Erro ao carregar disciplinas');
      setDisciplinas([]);
    } finally {
      setLoadingDropdown(prev => ({ ...prev, disciplina: false }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(`🔄 Campo ${name} alterado para:`, value, typeof value);
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleKeyPress = (e, nextRef) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (nextRef && nextRef.current) {
        nextRef.current.focus();
      }
    }
  };

  const validarFormulario = () => {
    const erros = [];

    if (!formData.extensao) erros.push('Extensão é obrigatória');
    if (!formData.aluno) erros.push('Aluno é obrigatório');
    if (!formData.curso) erros.push('Curso é obrigatório');
    if (!formData.disciplina) erros.push('Disciplina é obrigatória');
    if (!formData.dataRealizacao) erros.push('Data de Realização é obrigatória');
    if (!formData.cargaHoraria || formData.cargaHoraria <= 0) erros.push('Carga Horária deve ser maior que zero');
    if (formData.media === '' || formData.media < 0 || formData.media > 10) erros.push('Média deve estar entre 0 e 10');

    if (erros.length > 0) {
      erros.forEach(erro => toast.error(erro));
      return false;
    }

    return true;
  };

  const lancarNota = async (continuar = false) => {
    if (!validarFormulario()) return;

    try {
      setLoading(true);

      const payload = {
        idAluno: parseInt(formData.aluno),
        idDisciplina: parseInt(formData.disciplina),
        media: parseFloat(formData.media),
        cargaHoraria: parseInt(formData.cargaHoraria),
        dataRealizacao: formData.dataRealizacao,
        extensao: parseInt(formData.extensao),
        curso: parseInt(formData.curso)
      };

      console.log('📤 Enviando lançamento:', payload);

      const response = await api.post('/lancamento-notas/individual', payload);

      console.log('📥 Resposta recebida:', response);

      if (response.data.success) {
        toast.success(response.data.message);

        // Adicionar aos últimos lançamentos
        const alunoNome = alunos.find(a => a.idPessoa === parseInt(formData.aluno))?.nome || 'Desconhecido';
        const disciplinaNome = disciplinas.find(d => d.idProduto === parseInt(formData.disciplina))?.nome || 'Desconhecido';
        
        setUltimosLancamentos(prev => [
          {
            id: response.data.data.id,
            aluno: alunoNome,
            disciplina: disciplinaNome,
            media: formData.media,
            cargaHoraria: formData.cargaHoraria,
            dataRealizacao: formData.dataRealizacao,
            timestamp: new Date().toLocaleString('pt-BR')
          },
          ...prev
        ].slice(0, 10)); // Manter apenas últimos 10

        if (continuar) {
          // Limpar disciplina e média, manter extensão, aluno, curso, data e carga horária
          setFormData(prev => ({
            ...prev,
            disciplina: '',
            media: ''
          }));
          // Focar no campo disciplina para novo lançamento
          setTimeout(() => {
            const disciplinaInput = document.querySelector('.searchable-input[placeholder*="disciplina"]');
            if (disciplinaInput) disciplinaInput.focus();
          }, 100);
        } else {
          // Limpar tudo
          setFormData({
            extensao: '',
            aluno: '',
            curso: '',
            disciplina: '',
            dataRealizacao: new Date().toISOString().split('T')[0],
            cargaHoraria: '',
            media: ''
          });
          // Focar na extensão
          setTimeout(() => extensaoRef.current?.focus(), 100);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao lançar nota:', error);
      console.error('❌ Estrutura do erro:', {
        response: error.response,
        data: error.response?.data,
        status: error.response?.status,
        message: error.message
      });
      
      // Tentar extrair a mensagem de erro de várias formas possíveis
      let mensagemErro = 'Erro ao lançar nota';
      
      if (error.response?.data) {
        if (error.response.data.error) {
          mensagemErro = error.response.data.error;
        } else if (error.response.data.message) {
          mensagemErro = error.response.data.message;
        }
      } else if (error.message) {
        mensagemErro = error.message;
      }
      
      console.log('📢 Exibindo erro:', mensagemErro);
      toast.error(mensagemErro, {
        autoClose: 5000,
        position: 'top-center'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    lancarNota(false);
  };

  const handleLancarContinuar = (e) => {
    e.preventDefault();
    lancarNota(true);
  };

  const getStatusNota = (media) => {
    const m = parseFloat(media);
    if (m >= 6) return { texto: 'Aprovado', classe: 'status-aprovado' };
    if (m >= 5) return { texto: 'Recuperação', classe: 'status-recuperacao' };
    return { texto: 'Reprovado', classe: 'status-reprovado' };
  };

  return (
    <div className="lancamento-individual-container">
      <div className="page-header">
        <h1>Lançamento de Notas Individual</h1>
        <p className="subtitle">Lançamento rápido de notas e carga horária por disciplina</p>
      </div>

      <div className="content-wrapper">
        {/* Formulário */}
        <div className="form-card">
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              {/* Extensão */}
              <SearchableSelect
                label="EXTENSÃO"
                placeholder="Digite para buscar extensão..."
                value={formData.extensao}
                options={extensoes}
                onSelect={(value) => handleChange({ target: { name: 'extensao', value } })}
                loading={loadingDropdown.extensao}
                required
                valueKey="idExtensao"
                labelKey="nome"
              />

              {/* Aluno */}
              <SearchableSelect
                label="ALUNO"
                placeholder="Digite para buscar aluno..."
                value={formData.aluno}
                options={alunos}
                onSelect={(value) => handleChange({ target: { name: 'aluno', value } })}
                loading={loadingDropdown.aluno}
                disabled={!formData.extensao}
                required
                valueKey="idPessoa"
                labelKey="nome"
              />

              {/* Curso */}
              <div className="form-group">
                <label>
                  CURSO <span className="required">*</span>
                </label>
                <select
                  value={formData.curso}
                  onChange={(e) => handleChange({ target: { name: 'curso', value: e.target.value } })}
                  disabled={loadingDropdown.curso}
                  required
                >
                  <option value="">Selecione...</option>
                  {cursos.map(curso => (
                    <option key={curso.idCurso} value={curso.idCurso}>
                      {curso.nome}
                    </option>
                  ))}
                </select>
              </div>

              {/* Carga Horária */}
              <div className="form-group">
                <label htmlFor="cargaHoraria">
                  CARGA HORÁRIA <span className="required">*</span>
                </label>
                <input
                  type="number"
                  id="cargaHoraria"
                  name="cargaHoraria"
                  ref={cargaRef}
                  value={formData.cargaHoraria}
                  onChange={handleChange}
                  onKeyPress={(e) => handleKeyPress(e, mediaRef)}
                  min="1"
                  placeholder="Ex: 40"
                  required
                />
              </div>

              {/* Data de Realização */}
              <div className="form-group">
                <label htmlFor="dataRealizacao">
                  DATA DE REALIZAÇÃO <span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="dataRealizacao"
                  name="dataRealizacao"
                  ref={dataRef}
                  value={formData.dataRealizacao}
                  onChange={handleChange}
                  onKeyPress={(e) => handleKeyPress(e, cargaRef)}
                  required
                />
              </div>

              {/* Disciplina */}
              <SearchableSelect
                label="DISCIPLINA"
                placeholder="Digite para buscar disciplina..."
                value={formData.disciplina}
                options={disciplinas}
                onSelect={(value) => handleChange({ target: { name: 'disciplina', value } })}
                loading={loadingDropdown.disciplina}
                required
                valueKey="idProduto"
                labelKey="nome"
              />

              {/* Média */}
              <div className="form-group">
                <label htmlFor="media">
                  MÉDIA <span className="required">*</span>
                </label>
                <input
                  type="number"
                  id="media"
                  name="media"
                  ref={mediaRef}
                  value={formData.media}
                  onChange={handleChange}
                  step="0.1"
                  min="0"
                  max="10"
                  placeholder="Ex: 7.5"
                  required
                />
              </div>
            </div>

            {/* Botões */}
            <div className="form-actions">
              <button
                type="button"
                onClick={handleLancarContinuar}
                disabled={loading}
                className="btn btn-secondary"
              >
                {loading ? 'Lançando...' : 'Lançar e Continuar'}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? 'Lançando...' : 'Lançar e Limpar'}
              </button>
            </div>
          </form>
        </div>

        {/* Últimos Lançamentos */}
        {ultimosLancamentos.length > 0 && (
          <div className="ultimos-lancamentos">
            <h2>Últimos Lançamentos</h2>
            <div className="lancamentos-list">
              {ultimosLancamentos.map((lanc, index) => {
                const status = getStatusNota(lanc.media);
                return (
                  <div key={`${lanc.id}-${index}`} className="lancamento-item">
                    <div className="lancamento-header">
                      <span className="lancamento-aluno">{lanc.aluno}</span>
                      <span className={`lancamento-status ${status.classe}`}>
                        {status.texto}
                      </span>
                    </div>
                    <div className="lancamento-body">
                      <p className="lancamento-disciplina">{lanc.disciplina}</p>
                      <div className="lancamento-info">
                        <span>Média: <strong>{lanc.media}</strong></span>
                        <span>CH: <strong>{lanc.cargaHoraria}h</strong></span>
                        <span>Data: <strong>{lanc.dataRealizacao.split('-').reverse().join('/')}</strong></span>
                      </div>
                    </div>
                    <div className="lancamento-timestamp">
                      {lanc.timestamp}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}

export default LancamentoNotasIndividual;
